import * as ko from "knockout";
import {PageController} from "./PageController";
import {applyPageShellObjectData, suppressesPageShellHeadingBadges} from "./PageShellObjectSupport";
import {PageShellObjectActions, supportsPageShellObjectActions} from "./PageShellObjectActions";
import {IRouteDefinitionParams, IObjectData, IKnockoutComponent} from "../../index";
import {getObjectFromRouteData} from "../../util/request";
import {nsRoute} from "../../util/route";
import {global} from "../../appContext";
import {_d} from "../../util/misc";
import {getRouteLabel} from "../../constants/RouteDefinitions";
import {nsObjectRouteLink} from "../object/nsObjectRouteLink";
import {redirectToDefaultTab, hasPageShellContentWrapper} from "../../util/pageShellRoute";
import {buildPageShellHeaderActions, IPageShellHeaderActions} from "./pageShellHeaderActions";
import {IDropdownButton} from "../button";
import {IParams as IEllipsisDropdownParams} from "../button/ellipsis-dropdown";
import ParsedRouteDataDecorator = nsRoute.ParsedRouteDataDecorator;
import detailsHtml from './details.html?raw';

export let template = detailsHtml;

/** Scroll distance (px) over which the bottom shadow reaches full strength. */
const SCROLL_SHADOW_RANGE = 72;

function withHeadingActionChrome(button: IDropdownButton): IDropdownButton {
    return {
        ...button,
        cls: [button.cls, 'btn-action', 'btn-heading-action'].filter(Boolean).join(' '),
    };
}

export class viewModel extends ParsedRouteDataDecorator {
    private readonly routeData: KnockoutObservable<any | null> = ko.observable(null);
    /** 0–1, drives `--scroll-shadow-op` on the sticky root. */
    readonly scrollShadowOp: KnockoutObservable<number> = ko.observable(0);
    protected readonly isReady: KnockoutObservable<boolean | null> = ko.observable(null);
    private objectActions: PageShellObjectActions | null = null;
    private readonly objectActionsCtrl: KnockoutObservable<PageShellObjectActions | null> = ko.observable(null);
    private readonly headerActions = ko.pureComputed((): IPageShellHeaderActions => {
        const objectCtrl = this.objectActionsCtrl();
        if (objectCtrl) {
            return objectCtrl.headerActions();
        }
        const fromContent = this.__tabManager?.contentHeaderActions();
        if (fromContent && (fromContent.quick.length || fromContent.more.length)) {
            return fromContent;
        }
        return buildPageShellHeaderActions(this.__routeDef, this.routeData());
    });
    private readonly moreActions = ko.pureComputed((): IDropdownButton[] => {
        return this.headerActions().more.filter((btn) => btn.validator?.() ?? true);
    });
    private readonly moreDropdown: IEllipsisDropdownParams = {
        buttons: this.moreActions,
        icon: 'fal fa-chevron-down',
        label: 'pageshell.actions.more',
        title: 'pageshell.actions.more',
        btnClass: 'btn btn-action btn-heading-actions-more',
    };
    /** True when one content component hosts all tabs via route-destination (create/edit customer, etc.). */
    private readonly usesContentWrapper = hasPageShellContentWrapper(
        this.__routeDefinitionParams__.__shellRouteDef ?? this.__routeDef,
    );

    /**
     * Content-wrapper body: keep a stable {name,params} identity across tab soft-nav so the form
     * viewModel is not remounted (createcustomer was resetting to DefaultValues on every tab click).
     * Once mounted, never clear — a transient null currentComponent must not tear down the form.
     */
    private stableContentBody: IKnockoutComponent | null = null;
    private readonly contentWrapperBody = ko.pureComputed((): IKnockoutComponent | null => {
        if (!this.usesContentWrapper) {
            return null;
        }
        if (this.stableContentBody) {
            return this.stableContentBody;
        }
        const component = this.__tabManager?.currentComponent();
        if (!component?.name) {
            return null;
        }
        this.stableContentBody = {name: component.name, params: component.params};
        return this.stableContentBody;
    });

    /**
     * Per-tab body outlet: remount when the active tab route id changes.
     * Uses native `component` (not `_component`/proxy) so soft-nav tab swaps remount content reliably.
     */
    private stableTabBody: {key: string; component: IKnockoutComponent}[] = [];
    private readonly bodyOutletNodes = ko.pureComputed(() => {
        if (this.usesContentWrapper) {
            return [] as {key: string; component: IKnockoutComponent}[];
        }
        const component = this.__tabManager?.currentComponent();
        if (!component?.name) {
            this.stableTabBody = [];
            return this.stableTabBody;
        }
        const key = String(component.params?.__routeId ?? component.name);
        const prev = this.stableTabBody[0];
        if (prev?.key === key && prev.component.name === component.name) {
            return this.stableTabBody;
        }
        this.stableTabBody = [{
            key,
            component: {name: component.name, params: component.params},
        }];
        return this.stableTabBody;
    });

    private contentScrollEl: Element | null = null;
    private readonly contentScrollHandler = () => this.updateScrollShadow();

    constructor(params: IRouteDefinitionParams) {
        super(params);

        if (this.__routeDef?.component?.name === 'component/page-shell/details'
            && !params.__shellRouteDef
            && this.__routeDef.tabs?.length) {
            // Only stop when the URL was canonicalized; if the redirect was skipped (no org yet)
            // fall through and mount the shell so the bare deep link still renders.
            if (redirectToDefaultTab(this.__routeId, this.__routeParams ?? {})) {
                return;
            }
        }

        this.__tabManager = new PageController(this.__getParams({
            __componentId: this.__componentId,
        }));
        const tabManager = this.__tabManager;
        const cr = global.router.currentRoute();
        // Soft tab nav reuses this TabManager. Stash on construction params (RouteValidator's
        // cloneDeep cache) and on live currentRoute — createcustomer previously only attached to
        // `cr`, then lost the manager across soft-nav when `cr` was replaced.
        this.__routeDefinitionParams__.__tabManager = tabManager;
        global.routeValidator.notePageShellTabManager(tabManager);
        if (cr && (
            cr.__routeId === this.__routeId
            || cr.__shellRouteId === this.__routeId
            || cr.__shellRouteId === this.__shellRouteId
            || cr.__shellRouteDef?.id === this.__routeDef?.id
        )) {
            cr.__tabManager = tabManager;
        }
        this.__requestRouteData?.request()
            .then((data: any[]) => {
                const object = getObjectFromRouteData(data, this.__routeDef);
                this.routeData(object);
                if (this.__routeDef.objectTypeId && tabManager) {
                    return applyPageShellObjectData(tabManager, data as [IObjectData], this.__routeDef)
                        .then(() => this.bindObjectActions(object));
                }
                this.bindObjectActions(object);
            })
            .catch((reason: string) => _d.error(reason));
        this.initContentScrollListener();
    }

    /** Ghost chrome for the inline quick actions; destructive entries keep their own red treatment. */
    private getQuickActions = ko.pureComputed((): IDropdownButton[] => {
        if (this.__tabManager?.currentId() === "customersso") {
            return [];
        }
        return this.headerActions().quick
            .filter((button) => button.validator?.() ?? true)
            .map(withHeadingActionChrome);
    });

    private hasHeaderActions = ko.pureComputed((): boolean => {
        return this.getQuickActions().length > 0 || this.moreActions().length > 0;
    });

    /** No-tab shells (manual user/group/department/apiuser): Mer/quick sit on the heading row. */
    private showInlineHeaderActions = ko.pureComputed((): boolean => {
        return this.__routeDef.showTabs === false && this.hasHeaderActions();
    });

    /** Real tab strip only — do not keep an empty row just to host actions. */
    private showTabBar = ko.pureComputed((): boolean => {
        return this.__routeDef.showTabs !== false;
    });

    /** Attach object actions once route object data is available. */
    private bindObjectActions(object: IObjectData | null | undefined) {
        this.objectActions?.dispose();
        this.objectActions = null;
        this.objectActionsCtrl(null);
        if (!object || !supportsPageShellObjectActions(this.__routeDef)) {
            return;
        }
        this.objectActions = new PageShellObjectActions({
            object,
            shellRouteDef: this.__routeDef,
            routeParams: this.__routeParams ?? {},
            onObjectMutated: () => {
                this.__requestRouteData?.request()
                    .then((data: any[]) => {
                        const refreshed = getObjectFromRouteData(data, this.__routeDef);
                        this.routeData(refreshed);
                        this.bindObjectActions(refreshed);
                    })
                    .catch((reason: string) => _d.error(reason));
            },
        });
        this.objectActionsCtrl(this.objectActions);
        this.objectActions.start();
    }

    private initContentScrollListener() {
        window.requestAnimationFrame(() => {
            if (!this.tryAttachContentScroll()) {
                this.initContentScrollListener();
            }
        });
    }

    private tryAttachContentScroll(): boolean {
        const el = document.querySelector(".lm-c");
        if (!el) {
            return false;
        }
        if (this.contentScrollEl === el) {
            return true;
        }
        this.detachContentScroll();
        this.contentScrollEl = el;
        el.addEventListener("scroll", this.contentScrollHandler, {passive: true});
        this.updateScrollShadow();
        return true;
    }

    private detachContentScroll() {
        if (this.contentScrollEl) {
            this.contentScrollEl.removeEventListener("scroll", this.contentScrollHandler);
            this.contentScrollEl = null;
        }
    }

    private updateScrollShadow() {
        const el = this.contentScrollEl ?? document.querySelector(".lm-c");
        const top = el instanceof HTMLElement ? el.scrollTop : 0;
        const t = Math.min(1, top / SCROLL_SHADOW_RANGE);
        this.scrollShadowOp(t);
    }

    /** Warning when viewing a soft-deleted / deactivated object (user, group, department, …). */
    private showDeletedBanner() {
        return !!(this.routeData()?.deleted && this.__objDef);
    }

    private deletedBannerMessage() {
        return `message: disabled object|object:((${this.__objDef!.name}|ac))`;
    }

    private showLinked() {
        const linkableRouteId = this.getLinkableShellRouteId();
        const isQualifiedRoute = nsObjectRouteLink.LinkableRouteIds[linkableRouteId];
        const isRouteDataLoaded = this.routeData()?.type?.id;
        return !!isQualifiedRoute && isRouteDataLoaded;
    }

    /** PageShell child hashes use tab __routeId; linked-object UI keys off the shell parent. */
    private getLinkableShellRouteId(): nsObjectRouteLink.TLinkableRouteId {
        return (this.__shellRouteId ?? this.__routeDef?.id ?? this.__routeId) as nsObjectRouteLink.TLinkableRouteId;
    }

    private getRouteLabel = getRouteLabel;

    /** Heading params; create/edit shells suppress count/error badges on the main heading. */
    private getHeadingParams() {
        if (suppressesPageShellHeadingBadges(this.__routeDef)) {
            return this.__getParams({
                buttonManager: this.__tabManager!.buttonManager,
                badge: null,
                badgeError: null,
            });
        }
        return this.__getParams({
            buttonManager: this.__tabManager!.buttonManager,
        });
    }

    override dispose() {
        super.dispose();
        this.detachContentScroll();
        this.objectActions?.dispose();
        this.objectActions = null;
        this.objectActionsCtrl(null);
        this.hasHeaderActions.dispose();
        this.showInlineHeaderActions.dispose();
        this.showTabBar.dispose();
        this.getQuickActions.dispose();
        this.moreActions.dispose();
        this.headerActions.dispose();
        this.__tabManager?.dispose();
        // Router aborts getobject when leaving the entity; avoid aborting on soft tab swaps.
    }
}
